import { ApolloClient, gql } from "@apollo/client";

const AUTH_CHECK_QUERY = gql`
    query QuickAuthCheck { 
        userProfile { 
            id 
            email
        } 
    }
`;

export interface AuthValidationResult {
    isValid: boolean;
    error?: 'authentication_expired' | 'no_user_profile' | 'authentication_failed';
}

/**
 * Validates authentication state before critical operations like checkout
 * @param client Apollo Client instance
 * @returns Promise<AuthValidationResult>
 */
export const validateAuthenticationBeforeCheckout = async (
    client: ApolloClient<any>
): Promise<AuthValidationResult> => {
    try {
        console.log("🔐 Validating authentication before checkout...");
        
        const { data, errors } = await client.query({
            query: AUTH_CHECK_QUERY,
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        });
        
        if (errors?.some(e => 
            e.message.includes('UNAUTHENTICATED') || 
            e.message.includes('access denied') ||
            e.message.includes('jwt expired')
        )) {
            console.warn("🚨 Authentication validation failed: Authentication expired");
            return { isValid: false, error: 'authentication_expired' };
        }
        
        if (!data?.userProfile?.id) {
            console.warn("🚨 Authentication validation failed: No user profile");
            return { isValid: false, error: 'no_user_profile' };
        }
        
        console.log("✅ Authentication validation successful");
        return { isValid: true };
    } catch (error: any) {
        console.warn("⚠️ Authentication validation error:", error.message);
        
        if (error.message?.includes('UNAUTHENTICATED') || 
            error.message?.includes('jwt expired') ||
            error.message?.includes('access denied')) {
            return { isValid: false, error: 'authentication_failed' };
        }
        
        // For network issues or other errors, optimistically allow checkout
        // The actual checkout will handle auth errors properly
        console.warn('Auth validation failed due to network/other error, allowing checkout to proceed');
        return { isValid: true }; 
    }
};
